<script setup lang="ts">
const features = [
  {
    icon: 'bx-shield-alt-2',
    title: 'Veilige Omgeving',
    description: 'Veiligheid staat voorop. Al onze locaties zijn zorgvuldig geselecteerd en onze begeleiders zijn opgeleid in EHBO en veiligheidsprotocollen.',
    color: '#F27063',
    bgColor: '#FFF5F3',
  },
  {
    icon: 'bx-group',
    title: 'Kleine Groepen',
    description: 'We werken met kleine groepen van maximaal 12 kinderen per begeleider, zodat elk kind de persoonlijke aandacht krijgt die het verdient.',
    color: '#8FB5D9',
    bgColor: '#F3F8FC',
  },
  {
    icon: 'bx-bulb',
    title: 'Creatief Leren',
    description: 'Door speelse activiteiten leren kinderen samenwerken, probleemoplossend denken en hun creativiteit ontwikkelen op een natuurlijke manier.',
    color: '#FFD93D',
    bgColor: '#FFFBF0',
  },
  {
    icon: 'bx-heart',
    title: 'Persoonlijke Aandacht',
    description: 'Elk kind is uniek. We nemen de tijd om de interesses en behoeften van elk kind te leren kennen en daarop in te spelen.',
    color: '#77882B',
    bgColor: '#F7F9F1',
  },
  {
    icon: 'bx-star',
    title: 'Unieke Activiteiten',
    description: 'Van bouwen en knutselen tot sportieve uitdagingen en natuurontdekkingen - elk kamp biedt een uniek programma vol avontuur.',
    color: '#D89441',
    bgColor: '#FFF8F0',
  },
  {
    icon: 'bx-medal',
    title: 'Erkende Kwaliteit',
    description: 'Als erkende VZW voldoen we aan alle kwaliteitsnormen voor jeugdkampen en worden we regelmatig gecontroleerd door Kind & Gezin.',
    color: '#9C27B0',
    bgColor: '#F9F3FB',
  },
]
</script>

<template>
  <div class="features-section">
    <VContainer>
      <div class="section-header">
        <div class="section-badge">
          <VIcon icon="bx-check-circle" />
          <span>Waarom Kiezen Voor Ons</span>
        </div>
        <h2 class="section-title">
          Wat Maakt 't SpeelFabrik Uniek?
        </h2>
        <p class="section-description">
          't SpeelFabrik combineert veiligheid, creativiteit en plezier in een unieke kamp ervaring.
          Ontdek wat ons onderscheidt van andere kampen en waarom ouders steeds weer voor ons kiezen.
        </p>
      </div>

      <div class="features-grid">
        <div
          v-for="(feature, index) in features"
          :key="index"
          class="feature-card"
          :style="{ '--card-delay': `${index * 0.1}s` }"
        >
          <div
            class="feature-icon-wrapper"
            :style="{
              background: feature.bgColor,
            }"
          >
            <VIcon
              :icon="feature.icon"
              size="48"
              :style="{ color: feature.color }"
            />
            <div
              class="icon-glow"
              :style="{
                background: `${feature.color}20`,
              }"
            />
          </div>

          <h3 class="feature-title">
            {{ feature.title }}
          </h3>

          <p class="feature-description">
            {{ feature.description }}
          </p>

          <div class="feature-arrow">
            <VIcon
              icon="bx-right-arrow-alt"
              :style="{ color: feature.color }"
            />
          </div>
        </div>
      </div>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.features-section {
  position: relative;
  background: rgb(var(--v-theme-surface));
  padding-block: 6rem;
}

.section-header {
  position: relative;
  z-index: 1;
  margin-block-end: 4rem;
  text-align: center;

  .section-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 8px;
    background: rgba(242, 112, 99, 10%);
    color: #f27063;
    font-family: "Peachy Kee JF", "Public Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    gap: 0.5rem;
    letter-spacing: 0.5px;
    margin-block-end: 1.5rem;
    padding-block: 0.5rem;
    padding-inline: 1.5rem;
    text-transform: uppercase;

    .v-icon {
      color: currentcolor;
    }
  }

  .section-title {
    margin: 0;
    color: rgb(var(--v-theme-on-surface));
    font-family: "Peachy Kee JF", "Public Sans", sans-serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    line-height: 1.2;
    margin-block-end: 1rem;
  }

  .section-description {
    color: #546e7a;
    font-size: 1.125rem;
    line-height: 1.6;
    margin-inline: auto;
    max-inline-size: 700px;
  }
}

.features-grid {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.feature-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  block-size: 100px;
  inline-size: 100px;
  margin-block-end: 1.5rem;
  transition: transform 0.4s ease;

  .icon-glow {
    position: absolute;
    z-index: 0;
    inset: -20px;
  }

  .v-icon {
    position: relative;
    z-index: 1;
  }
}

.feature-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(242, 112, 99, 10%);
  block-size: 40px;
  inline-size: 40px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.feature-card {
  position: relative;
  overflow: hidden;
  padding: 2.5rem;
  border-radius: 24px;
  animation: fade-in-up 0.6s ease-out forwards;
  animation-delay: var(--card-delay);
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 6%);
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    position: absolute;
    background: #f27063;
    block-size: 4px;
    content: "";
    inline-size: 100%;
    inset-block-start: 0;
    inset-inline-start: 0;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 12%);
    transform: translateY(-8px);

    &::before {
      transform: scaleX(1);
    }

    .feature-icon-wrapper {
      transform: scale(1.1) rotate(5deg);
    }

    .feature-arrow {
      opacity: 1;
      transform: translateX(8px);
    }
  }
}

.feature-title {
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  margin-block-end: 1rem;
}

.feature-description {
  color: #546e7a;
  font-size: 1rem;
  line-height: 1.6;
  margin-block-end: 1rem;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .features-section {
    padding-block: 4rem;
  }

  .features-grid {
    gap: 1.5rem;
    grid-template-columns: 1fr;
  }

  .feature-card {
    padding: 2rem;
  }
}
</style>
